/**
 * Which of a room's rates a card leads with — shared by the room model, the web and mobile cards,
 * and both rate pickers.
 *
 * Why this exists: a card used to lead with the room's cheapest rate of ANY party, available or
 * not. At Andaz 5th Avenue (2026-10-04) that was a military rate priced for the family which a later
 * search no longer returned: the whole card read "Sold out", could not be tapped, and led with a
 * price nobody without a military ID could book — while the same suite was bookable for two adults.
 * The rules here are the same the server applies to what it hands the concierge
 * (`server/src/room-options/travelport-room-presentation.ts`): the party that was searched first,
 * only rates still available, and a gated rate only when nothing else is left.
 */

import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { occupancyKey } from '@elara/domain/models/schemas/room-schemas';

/** The rate fields these rules read; satisfied by both the domain rate and `RoomRateModel`. */
export interface ChoosableRate {
  occupancy?: Occupancy;
  available?: boolean;
  eligibility?: string;
  localPricing: { totalPrice: number };
}

const byPrice = <T extends ChoosableRate>(rates: T[]): T[] =>
  rates.toSorted((a, b) => a.localPricing.totalPrice - b.localPricing.totalPrice);

/**
 * The rate a card leads with: the cheapest still-available rate anyone can book, for the party the
 * search asked about; when that party has nothing left, the same for any party (the card then says
 * who the price is for); a gated rate (military, members only) only when nothing open is left.
 * Undefined when every rate is gone.
 */
export function pickLeadRate<T extends ChoosableRate>(rates: T[], searched: Occupancy | undefined): T | undefined {
  const live = rates.filter((r) => r.available !== false);
  if (live.length === 0) return undefined;
  const searchedKey = searched ? occupancyKey(searched) : undefined;
  const forSearched = searchedKey ? live.filter((r) => r.occupancy && occupancyKey(r.occupancy) === searchedKey) : [];
  const pool = byPrice(forSearched.length > 0 ? forSearched : live);
  return pool.find((r) => !r.eligibility) ?? pool[0];
}
